/** * 報告書カードコンポーネント */
<template>
  <div class="card-report">
    <!-- 報告者 -->
    <card-user
      can-user-name-click
      v-bind:user-id="reportUserId"
      v-bind:user-name="reportUserName"
      v-bind:profile-image-attach-file-type="reportUserProfileAttachFileType"
      v-bind:groups="groups"
      v-on:usercard-username-click="
        $store.dispatch('updateProfileUserId', $event)
      "
    />
    <div align="left" v-on:click="$_onClickCard" class="event-card-body">
      <div class="event-card-body-left">
        <!-- 報告書タイトル -->
        <div class="event-card-title">
          <icon-report class="icon-report" />
          <p v-on:click="$_onClickTitle">
            {{ reportTitle }}
          </p>
          <span v-if="existsAttachFiles" class="attach-file">
            {{ attachFilesCount }}
          </span>
        </div>
        <div class="update-at-text">{{ reportUpdateAt }}</div>
        <!-- 報告書コメント -->
        <p class="event-card-description">
          {{ reportComment }}
        </p>
        <!-- 詳細 -->
        <data-list v-bind:data="dataList" />
      </div>
      <div class="event-card-body-right">
        <!-- 未承認アイコン -->
        <div class="report-notice-box">
          <div
            v-if="!isChecked"
            class="label-unchecked"
            v-t="'appCardReport.unChecked'"
          />
        </div>
        <!-- 詳細ボタン -->
        <div class="report-action-box">
          <div
            v-if="!noDetailButton"
            class="button-detail btn-secondary"
            v-on:click="$_onClickDetailButton"
            v-t="'appCardReport.detail'"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DataList from '@/components/AppDataList.vue'
import CardUser from '@/components/AppCardUser.vue'
import { RestApiClient } from 'ifield-core-components'
import IconReport from '@/assets/ifield/ic_report.svg?component'
import { useDateDisplayText } from '@/composables/UseDateDisplayText'
import { toRefs } from 'vue'

/**
 * タイムラインタイプ（機能のタイプ)
 * @type {number}
 */
const TIMELINE_TYPE = 5

export default {
  name: 'AppCardReport',
  components: {
    'data-list': DataList,
    'card-user': CardUser,
    'icon-report': IconReport,
  },
  props: {
    /**
     * 詳細ボタンを非表示にする場合はtrue
     */
    noDetailButton: {
      type: Boolean,
      default: false,
    },
    /**
     * ID
     * (データを一意に識別することができるID。タイムラインID、報告書ID)
     */
    id: {
      type: Number,
      default: null,
      required: true,
    },
    /**
     * 報告書タイトル
     */
    reportTitle: {
      type: String,
      default: null,
      required: true,
    },
    /**
     * 報告書コメント
     */
    reportComment: {
      type: String,
      default: null,
      required: false,
    },
    /**
     * プレイス名
     */
    placeName: {
      type: String,
      default: null,
      required: false,
    },
    /**
     * プロジェクトID
     */
    projectId: {
      type: Number,
      default: null,
      required: false,
    },
    /**
     * プロジェクト名
     */
    projectName: {
      type: String,
      default: null,
      required: false,
    },
    /**
     * プロジェクトのファイルタイプ
     */
    projectImageAttachFileType: {
      type: String,
      default: null,
      required: true,
    },
    /**
     * 報告書が承認されているかどうか
     * (承認済みの場合はtrue)
     */
    isChecked: {
      type: Boolean,
      required: true,
    },
    /**
     * 添付ファイル数
     */
    attachFilesCount: {
      type: Number,
      default: null,
      required: true,
    },
    /**
     * 報告者ユーザーID
     */
    reportUserId: {
      type: Number,
      required: true,
    },
    /**
     * 報告者名
     */
    reportUserName: {
      type: String,
      required: true,
    },
    /**
     * 報告者ユーザープロファイルのファイルタイプ
     */
    reportUserProfileAttachFileType: {
      type: String,
      default: '9',
    },
    /**
     * 報告書更新日
     */
    updateAt: {
      type: String,
      required: true,
    },
    /**
     * 基準日時
     */
    baseDate: {
      type: Date,
      default: null,
    },
    /**
     * 所属グループ
     */
    groups: {
      type: Array,
      default: () => [],
    },
  },
  setup(props) {
    // 報告日時の表示テキスト
    const { updateAt, baseDate } = toRefs(props)
    const { text } = useDateDisplayText({ date: updateAt, baseDate })
    return { reportUpdateAt: text }
  },
  data: function () {
    return {}
  },
  computed: {
    /**
     * 添付ファイルが存在している場合はtrueを返却する
     * @return {boolean}
     */
    existsAttachFiles: function () {
      return this.attachFilesCount > 0
    },

    /**
     * アイコンの画像ダウンロードAPIのURL
     * @return {string}
     */
    iconImageUrl: function () {
      // ダウンロードファイルAPI
      let url = RestApiClient.createApiUrl('download_file')
      let params = url.searchParams
      params.append('attach_file_type', this.projectImageAttachFileType)
      params.append('report_project_image', 1)
      if (this.projectId) {
        params.append('id', this.projectId)
      }
      return url.toString()
    },

    /**
     * DataListコンポーネントに設定するデータを取得する
     */
    dataList: function () {
      return {
        standard: [
          {
            label: this.$t('appCardReport.field.place'),
            value: this.placeName,
          },
          {
            label: this.$t('appCardReport.field.project'),
            value: this.projectName,
          },
        ],
      }
    },
  },

  methods: {
    /**
     * クリック時のイベント
     */
    $_onClickCard: function () {
      this.$emit('reportcard-click', TIMELINE_TYPE, this.id)
    },

    /**
     * タイトルクリック時のイベント
     */
    $_onClickTitle: function (event) {
      event.stopPropagation()
      this.$emit('reportcard-title-click', TIMELINE_TYPE, this.id)
    },

    /**
     * 詳細ボタンクリック時のイベント
     */
    $_onClickDetailButton: function (event) {
      event.stopPropagation()
      this.$emit('reportcard-detail-click', TIMELINE_TYPE, this.id)
    },
  },
}
</script>

<style lang="scss" scoped>
::v-deep .card-user {
  padding-right: 8px;
}

.event-card-body {
  display: flex;
  justify-content: space-between;
  width: 100%;
  height: auto;
  padding: 0;
  margin: 10px 0 0 0;
  background-color: #f6f6f6;
  border-radius: 2px;
  overflow: hidden;
  transition: 0.2s;
  cursor: pointer;

  &:hover {
    background-color: #f0f0f0;
  }
}

.event-card-body-left {
  height: 100%;
  width: calc(100% - 100px);
  padding: 24px 10px 0 20px;
  .update-at-text {
    font-size: $font-size-small;
    color: #959595;
    margin: 8px 0 16px 0;
  }
}

.event-card-body-right {
  position: relative;
  width: 100px;
}

.event-card-title {
  display: flex;
  justify-content: flex-start;
  align-items: center;

  p {
    color: $text-color-link;
    font-weight: bold;
    font-size: $font-size-middle !important;
    max-width: calc(100% - 75px);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin-bottom: 0;
    line-height: 20px;

    &:hover {
      text-decoration: underline;
    }

    a {
      color: $text-color-link;
      font-weight: bold;
      margin-right: 4px;
    }
  }
}

.data-title-icon {
  width: 20px;
  height: 20px;
  margin-right: 8px;
}

span.attach-file {
  color: #808080;
  margin-left: 10px;
  font-size: $font-size-middle;
  line-height: 20px;

  &:before {
    content: '';
    display: inline-block;
    width: 16px;
    height: 16px;
    margin-right: 2px;
    background: url('../../assets/ifield/icon_attach_file.png') no-repeat;
    background-size: 16px 16px;
    position: relative;
    top: 2px;
  }
}

.event-card-description {
  margin-bottom: 15px;
  color: #4d4d4d;
  font-size: 0.8em;
  line-height: 1.4;
  // 2行分の高さ(font-size * line-height * 行数)
  max-height: calc(0.8em * 1.4 * 2);

  @supports (-webkit-line-clamp: 2) {
    display: -webkit-box;
    overflow: hidden;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
}

.report-notice-box {
  padding: 20px 20px 20px 0;
  width: 100%;
}

.label-unchecked {
  background-color: #c62f4d;
  border-radius: 3px;
  min-width: 55px;
  max-width: 70px;
  width: fit-content;
  height: 20px;
  margin: 0 0 0 auto;
  padding: 0 8px;
  color: #ffffff;
  font-size: 0.6em;
  font-weight: bold;
  text-align: center;
  line-height: 20px;
}

.report-action-box {
  bottom: 20px;
  position: absolute;
  width: 100%;
  padding: 0 20px;
}

.button-detail {
  border-radius: 3px;
  width: 55px;
  height: 20px;
  margin: 0 0 0 auto;
  font-size: 0.6em;
  font-weight: bold;
  text-align: center;
  line-height: 20px;
}

.icon-report {
  height: 20px;
  width: 20px;
  padding: 3px;
  margin-right: 12px;
  border-radius: 50%;
  position: relative;
  top: 0;
  fill: $entity-color-white;
  background-color: $entity-color-blue;
}
</style>
